/**
 * Lightweight presentational primitives for the prototype.
 *
 * Built on MUI `Box`/`Typography` with the local zinc/accent palette so the
 * cards, pills, chips and risk boxes match the mockups without fighting the
 * app's global MUI component overrides.
 */

import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type React from "react";

import { accent, text, zinc } from "../colors";
import type { DriverStatusCode, TagKind, Urgency } from "../types";

export function Panel({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        bgcolor: zinc[900],
        border: `1px solid ${zinc[800]}`,
        borderRadius: "12px",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function Pill({ label, active, onClick }: { label: string; active?: boolean; onClick?: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        px: 1.5,
        py: 0.75,
        borderRadius: "999px",
        border: `1px solid ${active ? accent.redBorder : zinc[700]}`,
        color: active ? accent.red : text.secondary,
        bgcolor: active ? accent.redSoft : "transparent",
        transition: "all 120ms ease",
        "&:hover": { borderColor: active ? accent.redBorder : zinc[600], color: active ? accent.red : text.primary },
      }}
    >
      {label}
    </Box>
  );
}

type ChipTone = "red" | "amber" | "green" | "purple" | "blue" | "neutral";

const TONE: Record<ChipTone, { fg: string; bg: string; border: string }> = {
  red: { fg: accent.red, bg: accent.redSoft, border: accent.redBorder },
  amber: { fg: accent.amber, bg: accent.amberSoft, border: accent.amberBorder },
  green: { fg: accent.green, bg: accent.greenSoft, border: accent.greenBorder },
  purple: { fg: accent.purple, bg: accent.purpleSoft, border: accent.purpleBorder },
  blue: { fg: accent.blue, bg: accent.blueSoft, border: accent.blueBorder },
  neutral: { fg: text.secondary, bg: zinc[800], border: zinc[700] },
};

export function Chip({
  label,
  tone = "neutral",
  icon,
  sx,
}: {
  label: string;
  tone?: ChipTone;
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const t = TONE[tone];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.375,
        borderRadius: "6px",
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        color: t.fg,
        bgcolor: t.bg,
        border: `1px solid ${t.border}`,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

const URGENCY_TONE: Record<Urgency, ChipTone> = {
  urgent: "red",
  high: "amber",
  med: "neutral",
};

const URGENCY_LABEL: Record<Urgency, string> = {
  urgent: "URGENT",
  high: "HIGH",
  med: "MED",
};

export function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  return <Chip label={URGENCY_LABEL[urgency]} tone={URGENCY_TONE[urgency]} />;
}

const TAG_META: Record<TagKind, { label: string; tone: ChipTone }> = {
  pta: { label: "PTA", tone: "red" },
  empty_call: { label: "Empty call", tone: "amber" },
  ddi: { label: "DDI", tone: "red" },
  split_sleeper: { label: "Split sleeper", tone: "purple" },
  home_time: { label: "Home time", tone: "blue" },
  traffic: { label: "Traffic", tone: "blue" },
  weather: { label: "Weather", tone: "neutral" },
};

export function TagChip({ tag }: { tag: TagKind }) {
  const meta = TAG_META[tag];
  return <Chip label={meta.label} tone={meta.tone} />;
}

const STATUS_COLOR: Record<DriverStatusCode, string> = {
  AC: accent.green,
  C: accent.amber,
  T: accent.blue,
  BD: accent.red,
};

export function StatusDot({ status }: { status: DriverStatusCode }) {
  return (
    <Box
      sx={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: STATUS_COLOR[status],
      }}
    />
  );
}

/**
 * Small monospace badge holding a 6-letter reference code, shown inline beside a
 * customer name or location. Renders nothing when no code is available.
 */
export function CodeBadge({ code }: { code: string | null | undefined }) {
  if (!code) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        ml: 0.625,
        px: 0.5,
        py: "1px",
        borderRadius: "4px",
        bgcolor: zinc[800],
        border: `1px solid ${zinc[700]}`,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        lineHeight: 1.4,
        color: text.secondary,
        verticalAlign: "middle",
      }}
    >
      {code}
    </Box>
  );
}

export function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: text.muted }}
    >
      {children}
    </Typography>
  );
}

export function ActionButton({
  label,
  icon,
  variant = "primary",
  onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  variant?: "primary" | "ghost";
  onClick?: () => void;
}) {
  const primary = variant === "primary";
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        cursor: "pointer",
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        fontSize: 13,
        fontWeight: 700,
        px: 1.75,
        py: 0.875,
        borderRadius: "8px",
        border: `1px solid ${primary ? accent.blue : zinc[700]}`,
        color: primary ? "#fff" : text.primary,
        bgcolor: primary ? accent.blue : "transparent",
        transition: "all 120ms ease",
        "&:hover": {
          bgcolor: primary ? "#2f6fe0" : zinc[800],
          borderColor: primary ? "#2f6fe0" : zinc[600],
        },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}
